import React, { useEffect, useState, useRef } from 'react';
import { Table, Card, Spinner, Alert, Button, Modal, Badge, ProgressBar, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic, faUser, faSync, faCheck, faTimes, faInfoCircle, faSearch, faTrash, faCopy } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from '../../utils/setupAxios';
import Pagination from '../common/Pagination';
import { mergeFromDevice, deleteAllSyncSongs } from '../../redux/actions/syncActions';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardFooter,
} from '../common/ListMobileLayout';

const SyncUsers = () => {
  const dispatch = useDispatch();
  const { mergeLoading, mergeData, mergeError, deleteLoading, deleteData, deleteError } = useSelector((state) => state.sync);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);

  const [songsModalUser, setSongsModalUser] = useState(null);
  const [songs, setSongs] = useState([]);
  const [songsLoading, setSongsLoading] = useState(false);
  const [songsError, setSongsError] = useState(null);
  const [songsPage, setSongsPage] = useState(1);
  const [songsPageSize] = useState(10);
  const [songsTotal, setSongsTotal] = useState(0);
  const [deletingSongId, setDeletingSongId] = useState(null);

  // Merge modal state
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeProgress, setMergeProgress] = useState(0);
  const [mergePreviewData, setMergePreviewData] = useState(null);
  const [mergeStep, setMergeStep] = useState('idle'); // idle, input, preview, confirming, merging, completed
  const [currentMergeUser, setCurrentMergeUser] = useState(null);
  const [deviceIdInput, setDeviceIdInput] = useState('');
  const [previewSongsPage, setPreviewSongsPage] = useState(1);
  const [previewSongsPageSize] = useState(10);
  const [previewSongsSearchTerm, setPreviewSongsSearchTerm] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());

  const [copiedUserId, setCopiedUserId] = useState(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [deleteStep, setDeleteStep] = useState('idle'); // idle, confirming, deleting, completed
  const [currentDeleteUser, setCurrentDeleteUser] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { 
        page: currentPage, 
        items_per_page: pageSize 
      };
      
      // Add search param if debounced search term exists
      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      
      const res = await axiosInstance.get('/songs/admin/users', { params });
      const data = res.data?.data;
      setUsers(data?.items || []);
      const objs = data?.objects;
      setTotal(objs?.total ?? objs?.count ?? 0);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch users');
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search term - delay 500ms before updating debouncedSearchTerm
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Reset to page 1 when debounced search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, debouncedSearchTerm]);

  const openSongsModal = (user) => {
    setSongsModalUser(user);
    setSongsPage(1);
    setSongs([]);
    setSongsTotal(0);
    setSongsError(null);
  };

  const closeSongsModal = () => {
    setSongsModalUser(null);
    setSongs([]);
    setSongsPage(1);
    setSongsTotal(0);
    setSongsError(null);
  };

  const fetchUserSongs = async () => {
    if (!songsModalUser) return;
    setSongsLoading(true);
    setSongsError(null);
    try {
      const params = {
        user_id: songsModalUser.id,
        page: songsPage,
        items_per_page: songsPageSize
      };
      const res = await axiosInstance.get('/songs/admin/songs-by-user', { params });
      const data = res.data?.data;
      setSongs(data?.items || []);
      const objs = data?.objects;
      setSongsTotal(objs?.total ?? objs?.count ?? 0);
    } catch (err) {
      setSongsError(err.response?.data?.message || err.message || 'Failed to fetch songs');
      setSongs([]);
      setSongsTotal(0);
    } finally {
      setSongsLoading(false);
    }
  };

  useEffect(() => {
    if (songsModalUser) {
      fetchUserSongs();
    }
  }, [songsModalUser?.id, songsPage]);

  const handleUserPageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSongsPageChange = (page) => {
    setSongsPage(page);
  };

  const getProviderBadgeVariant = (provider) => {
    if (!provider) return 'secondary';
    const p = (provider || '').toLowerCase();
    if (p === 'google') return 'danger';
    if (p === 'facebook') return 'primary';
    if (p === 'apple') return 'dark';
    return 'secondary';
  };

  const handleDeleteSyncSong = async (songId) => {
    if (songId == null) return;
    setDeletingSongId(songId);
    setSongsError(null);
    try {
      const res = await axiosInstance.post('/songs/admin/delete-sync-song', { id: Number(songId) });
      const data = res.data?.data;
      if (res.data?.success && data?.deleted) {
        setSongs((prev) => prev.filter((s) => (s.id ?? s.task_id) !== songId));
        setSongsTotal((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete sync song';
      setSongsError(msg);
    } finally {
      setDeletingSongId(null);
    }
  };

  const PLACEHOLDER_TITLES = new Set(['Untitled Track', 'Untitled Song', 'Untitled', '']);

  const isSongTaskIdPlaceholderTitle = (title) => {
    const normalized = (title ?? '').trim();
    return /^Song [a-zA-Z0-9_-]+$/i.test(normalized);
  };

  const isPlaceholderTitle = (title) => {
    const normalized = (title ?? '').trim();
    return PLACEHOLDER_TITLES.has(normalized) || isSongTaskIdPlaceholderTitle(normalized);
  };

  const resolveSongTitle = (item) => {
    let title = item?.title;
    if (!isPlaceholderTitle(title)) return title.trim();

    if (!isPlaceholderTitle(item?.name)) return item.name.trim();

    try {
      const result = typeof item?.result === 'string' ? JSON.parse(item.result) : item?.result;
      if (result && typeof result === 'object') {
        const fromResult = result.name || result.title;
        if (!isPlaceholderTitle(fromResult)) return fromResult.trim();
      }
    } catch (_) {}

    try {
      const input = typeof item?.input_data === 'string' ? JSON.parse(item.input_data) : item?.input_data;
      if (input && typeof input === 'object') {
        const fromInput = input.title || input.song_title || input.name;
        if (!isPlaceholderTitle(fromInput)) return fromInput.trim();
      }
    } catch (_) {}

    return 'Untitled Track';
  };

  const matchesPreviewSongSearch = (song, term) => {
    const q = term.trim().toLowerCase();
    if (!q) return true;

    if (song.task_id && String(song.task_id).toLowerCase().includes(q)) {
      return true;
    }

    const title = resolveSongTitle(song).toLowerCase();
    if (title.includes(q)) return true;

    if (song.name && String(song.name).toLowerCase().includes(q)) return true;
    if (song.title && String(song.title).toLowerCase().includes(q)) return true;

    return false;
  };

  const getFilteredPreviewSongs = () => {
    const songs = mergePreviewData?.preview_songs || [];
    if (!previewSongsSearchTerm.trim()) return songs;
    return songs.filter((song) => matchesPreviewSongSearch(song, previewSongsSearchTerm));
  };

  const STEM_LINK_LABELS = {
    vocal_url: 'Vocals',
    instrumental_url: 'Instrumental',
    backing_vocals_url: 'Backing Vocals',
    drums_url: 'Drums',
    bass_url: 'Bass',
    guitar_url: 'Guitar',
    keyboard_url: 'Keyboard',
    percussion_url: 'Percussion',
    strings_url: 'Strings',
    synth_url: 'Synth',
    fx_url: 'FX',
    brass_url: 'Brass',
    woodwinds_url: 'Woodwinds',
    piano_url: 'Piano',
    vocals_url: 'Vocals',
    other_url: 'Other',
  };

  const parseSongItem = (item) => {
    let title = item.title;
    let thumbnail_url = item.thumbnail_url;
    let duration = item.duration;
    let result_url = null;
    let stems = null;
    try {
      if (item.result && typeof item.result === 'string') {
        const parsed = JSON.parse(item.result);
        if (!title && parsed.name) title = parsed.name;
        if (!thumbnail_url && parsed.thumbnail_url) thumbnail_url = parsed.thumbnail_url;
        if (!duration && parsed.duration) duration = parsed.duration;
        if (parsed.result_url) result_url = parsed.result_url;
        if (parsed.stems && typeof parsed.stems === 'object') {
          stems = parsed.stems;
          if (!result_url) {
            result_url = parsed.stems.vocal_url || parsed.stems.vocals_url || null;
          }
        }
      }
    } catch (_) {}
    if (!result_url && item.result_url) result_url = item.result_url;
    return {
      title: resolveSongTitle({ ...item, title }),
      thumbnail_url,
      duration,
      result_url,
      stems,
      service: item.service,
      type: item.type
    };
  };

  const handleMergeClick = (user) => {
    setCurrentMergeUser(user);
    setMergeStep('input');
    setMergeProgress(0);
    setMergePreviewData(null);
    setDeviceIdInput('');
    setPreviewSongsPage(1);
    setPreviewSongsSearchTerm('');
    setShowMergeModal(true);
  };

  const handleDeviceIdSubmit = async (e) => {
    e.preventDefault();
    if (!deviceIdInput.trim() || !currentMergeUser) {
      return;
    }
    
    setMergeStep('preview');
    setMergeProgress(0);
    await startMergePreview(deviceIdInput.trim(), currentMergeUser.id);
  };

  const startMergePreview = async (deviceId, userId) => {
    // Simulate progress: 5s -> 10s
    const duration = 5000 + Math.random() * 5000; // 5-10 seconds
    const interval = 100; // Update every 100ms
    const increment = 100 / (duration / interval);
    
    const progressInterval = setInterval(() => {
      setMergeProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return Math.min(prev + increment, 100);
      });
    }, interval);

    try {
      // Call dry-run API
      const result = await dispatch(mergeFromDevice(deviceId, userId, true, 1, 1000));
      clearInterval(progressInterval);
      setMergeProgress(100);
      setMergePreviewData(result.data);
      // Select all songs by default
      const allIds = new Set((result.data?.preview_songs || []).map((s) => s.task_id).filter(Boolean));
      setSelectedTaskIds(allIds);
      setMergeStep('confirming');
    } catch (error) {
      clearInterval(progressInterval);
      setMergeProgress(100);
      setMergeStep('confirming');
    }
  };

  const handleMergeConfirm = async () => {
    if (!currentMergeUser || !deviceIdInput.trim()) return;
    
    setMergeStep('merging');
    setMergeProgress(0);
    
    // Simulate progress for actual merge
    const duration = 5000 + Math.random() * 5000;
    const interval = 100;
    const increment = 100 / (duration / interval);
    
    const progressInterval = setInterval(() => {
      setMergeProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return Math.min(prev + increment, 100);
      });
    }, interval);

    const taskIdsToMerge = selectedTaskIds.size > 0 ? Array.from(selectedTaskIds) : null;

    try {
      const result = await dispatch(mergeFromDevice(deviceIdInput.trim(), currentMergeUser.id, false, 1, 1000, taskIdsToMerge));
      clearInterval(progressInterval);
      setMergeProgress(100);
      setMergeStep('completed');
      
      // Refresh users list
      fetchUsers();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        handleCloseMergeModal();
      }, 2000);
    } catch (error) {
      clearInterval(progressInterval);
      setMergeProgress(100);
      setMergeStep('completed');
    }
  };

  const handleMergeCancel = () => {
    handleCloseMergeModal();
  };

  const handleCloseMergeModal = () => {
    setShowMergeModal(false);
    setMergeStep('idle');
    setMergeProgress(0);
    setMergePreviewData(null);
    setCurrentMergeUser(null);
    setDeviceIdInput('');
    setPreviewSongsPage(1);
    setPreviewSongsSearchTerm('');
    setSelectedTaskIds(new Set());
  };

  const handlePreviewSongsPageChange = (page) => {
    setPreviewSongsPage(page);
  };

  const handleCopyLink = (user) => {
    const shareUrl = `https://song.appixi.net/songs/share/users/${user.alias_id}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopiedUserId(user.id);
      setTimeout(() => setCopiedUserId(null), 2000);
    });
  };

  const handleDeleteClick = (user) => {
    setCurrentDeleteUser(user);
    setDeleteStep('confirming');
    setDeleteProgress(0);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!currentDeleteUser) return;
    
    setDeleteStep('deleting');
    setDeleteProgress(0);
    
    // Simulate progress
    const duration = 5000 + Math.random() * 5000;
    const interval = 100;
    const increment = 100 / (duration / interval);
    
    const progressInterval = setInterval(() => {
      setDeleteProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return Math.min(prev + increment, 100);
      });
    }, interval);

    try {
      const result = await dispatch(deleteAllSyncSongs(currentDeleteUser.id));
      clearInterval(progressInterval);
      setDeleteProgress(100);
      setDeleteStep('completed');
      
      // Refresh users list
      fetchUsers();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        handleCloseDeleteModal();
      }, 2000);
    } catch (error) {
      clearInterval(progressInterval);
      setDeleteProgress(100);
      setDeleteStep('completed');
    }
  };

  const handleDeleteCancel = () => {
    handleCloseDeleteModal();
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteStep('idle');
    setDeleteProgress(0);
    setCurrentDeleteUser(null);
  };

  const handleToggleSong = (taskId) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const songs = getFilteredPreviewSongs();
    const ids = songs.map((s) => s.task_id).filter(Boolean);
    if (previewSongsSearchTerm.trim()) {
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
    } else {
      setSelectedTaskIds(new Set(ids));
    }
  };

  const handleDeselectAll = () => {
    const songs = getFilteredPreviewSongs();
    const idsToRemove = new Set(songs.map((s) => s.task_id).filter(Boolean));
    if (previewSongsSearchTerm.trim()) {
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        idsToRemove.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedTaskIds(new Set());
    }
  };

  const handlePreviewSongsSearchChange = (value) => {
    setPreviewSongsSearchTerm(value);
    setPreviewSongsPage(1);
  };

  const renderMergeModal = () => {
    const filteredPreviewSongs = getFilteredPreviewSongs();
    const totalPreviewSongs = mergePreviewData?.preview_songs?.length || 0;

    return (
      <Modal show={showMergeModal} onHide={handleCloseMergeModal} size="xl" centered scrollable fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faSync} className="me-2" />
            Merge Songs from Device
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mergeStep === 'input' && (
            <div>
              <Alert variant="info" className="mb-3">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                <strong>Enter Device ID to merge songs</strong>
              </Alert>
              
              {currentMergeUser && (
                <div className="mb-3">
                  <p><strong>User:</strong> {currentMergeUser.name || currentMergeUser.email || `User #${currentMergeUser.id}`}</p>
                  <p><strong>User ID:</strong> {currentMergeUser.id}</p>
                </div>
              )}

              <Form onSubmit={handleDeviceIdSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Device ID <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={deviceIdInput}
                    onChange={(e) => setDeviceIdInput(e.target.value)}
                    placeholder="Enter Device ID"
                    required
                    autoFocus
                  />
                  <Form.Text className="text-muted">
                    Enter the Device ID of the device to merge songs
                  </Form.Text>
                </Form.Group>
                
                <div className="d-flex gap-2 justify-content-end">
                  <Button variant="secondary" onClick={handleCloseMergeModal}>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={!deviceIdInput.trim()}>
                    <FontAwesomeIcon icon={faSync} className="me-2" />
                    Preview Merge
                  </Button>
                </div>
              </Form>
            </div>
          )}

          {mergeStep === 'preview' && (
            <div>
              <div className="mb-3">
                <p className="mb-2">Checking data to be merged...</p>
                <ProgressBar now={mergeProgress} label={`${Math.round(mergeProgress)}%`} />
              </div>
              {mergeLoading && (
                <div className="text-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Processing...</span>
                </div>
              )}
            </div>
          )}

          {mergeStep === 'confirming' && mergePreviewData && (
            <div>
              <Alert variant="info" className="mb-3">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                <strong>Preview Result:</strong>
              </Alert>
              
              <div className="mb-3">
                <p><strong>Data will be merged as follows:</strong></p>
                <div className="p-3 bg-light rounded">
                  <div className="row mb-2">
                    <div className="col-6"><strong>Total Processed:</strong></div>
                    <div className="col-6">{mergePreviewData.summary?.total_processed || 0}</div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-6"><strong>Will Create:</strong></div>
                    <div className="col-6">
                      <Badge bg="success">{mergePreviewData.summary?.created || 0}</Badge>
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-6"><strong>Will Update:</strong></div>
                    <div className="col-6">
                      <Badge bg="warning">{mergePreviewData.summary?.updated || 0}</Badge>
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-6"><strong>Will Delete:</strong></div>
                    <div className="col-6">
                      <Badge bg="danger">{mergePreviewData.summary?.deleted || 0}</Badge>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6"><strong>Errors:</strong></div>
                    <div className="col-6">
                      <Badge bg={mergePreviewData.summary?.errors_count > 0 ? 'danger' : 'success'}>
                        {mergePreviewData.summary?.errors_count || 0}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {mergePreviewData.preview_songs && mergePreviewData.preview_songs.length > 0 && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>
                      Songs to merge ({selectedTaskIds.size} / {totalPreviewSongs} selected
                      {previewSongsSearchTerm.trim() && (
                        <span className="text-muted fw-normal">
                          {' '}· showing {filteredPreviewSongs.length} match{filteredPreviewSongs.length !== 1 ? 'es' : ''}
                        </span>
                      )}
                      ):
                    </strong>
                    <div className="d-flex gap-2">
                      <Button variant="outline-primary" size="sm" onClick={handleSelectAll}>
                        Select All
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={handleDeselectAll}>
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  <Form.Group className="mb-2">
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faSearch} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search by name or task ID..."
                        value={previewSongsSearchTerm}
                        onChange={(e) => handlePreviewSongsSearchChange(e.target.value)}
                      />
                      {previewSongsSearchTerm && (
                        <Button
                          variant="outline-secondary"
                          onClick={() => handlePreviewSongsSearchChange('')}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      )}
                    </InputGroup>
                  </Form.Group>
                  <Pagination
                    currentPage={previewSongsPage}
                    totalItems={filteredPreviewSongs.length}
                    itemsPerPage={previewSongsPageSize}
                    onPageChange={handlePreviewSongsPageChange}
                    position="top"
                  />
                  <div className="mt-2 table-responsive" style={{ maxHeight: 'min(400px, 50vh)', overflowY: 'auto' }}>
                    <Table striped bordered size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}>
                            <Form.Check
                              type="checkbox"
                              checked={
                                filteredPreviewSongs.length > 0 &&
                                filteredPreviewSongs.every((s) => s.task_id && selectedTaskIds.has(s.task_id))
                              }
                              onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                            />
                          </th>
                          <th style={{ width: 48 }}>#</th>
                          <th>Title</th>
                          <th style={{ minWidth: 140 }}>Task ID</th>
                          <th>Service</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPreviewSongs.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-3">
                              No songs match your search.
                            </td>
                          </tr>
                        ) : filteredPreviewSongs
                          .slice(
                            (previewSongsPage - 1) * previewSongsPageSize,
                            previewSongsPage * previewSongsPageSize
                          )
                          .map((song, idx) => {
                            const globalIndex = (previewSongsPage - 1) * previewSongsPageSize + idx + 1;
                            const isSelected = song.task_id ? selectedTaskIds.has(song.task_id) : false;
                            return (
                              <tr
                                key={song.task_id || idx}
                                onClick={() => song.task_id && handleToggleSong(song.task_id)}
                                style={{ cursor: song.task_id ? 'pointer' : 'default' }}
                                className={isSelected ? 'table-active' : ''}
                              >
                                <td onClick={(e) => e.stopPropagation()}>
                                  <Form.Check
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={!song.task_id}
                                    onChange={() => song.task_id && handleToggleSong(song.task_id)}
                                  />
                                </td>
                                <td>{globalIndex}</td>
                                <td>{resolveSongTitle(song)}</td>
                                <td className="text-muted small font-monospace text-break">
                                  {song.task_id || '—'}
                                </td>
                                <td>{song.service || 'N/A'}</td>
                                <td>
                                  <Badge bg={song.status === 'done' ? 'success' : 'warning'}>
                                    {song.status || 'N/A'}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge bg={song.action === 'create' ? 'success' : song.action === 'update' ? 'info' : 'secondary'}>
                                    {song.action || 'N/A'}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  </div>
                  <Pagination
                    currentPage={previewSongsPage}
                    totalItems={filteredPreviewSongs.length}
                    itemsPerPage={previewSongsPageSize}
                    onPageChange={handlePreviewSongsPageChange}
                    position="bottom"
                  />
                </div>
              )}

              {mergeError && (
                <Alert variant="danger" className="mb-3">
                  <strong>Error:</strong> {mergeError}
                </Alert>
              )}

              <div className="text-center mt-4">
                <p className="mb-3">
                  <strong>
                    {selectedTaskIds.size === 0
                      ? 'No songs selected. Please select at least one song.'
                      : `Merge ${selectedTaskIds.size} selected song${selectedTaskIds.size !== 1 ? 's' : ''}?`}
                  </strong>
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  <Button variant="success" onClick={handleMergeConfirm} disabled={selectedTaskIds.size === 0}>
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    Yes
                  </Button>
                  <Button variant="danger" onClick={handleMergeCancel}>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    No
                  </Button>
                </div>
              </div>
            </div>
          )}

          {mergeStep === 'merging' && (
            <div>
              <div className="mb-3">
                <p className="mb-2">Merging data...</p>
                <ProgressBar now={mergeProgress} label={`${Math.round(mergeProgress)}%`} />
              </div>
              {mergeLoading && (
                <div className="text-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Processing...</span>
                </div>
              )}
            </div>
          )}

          {mergeStep === 'completed' && (
            <div>
              {mergeError ? (
                <Alert variant="danger">
                  <strong>Error:</strong> {mergeError}
                </Alert>
              ) : (
                <Alert variant="success">
                  <strong>Success!</strong> Merge completed.
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div className="dashboard-list-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Sync Users</h2>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Form.Group>
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              )}
            </InputGroup>
          </Form.Group>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="list-pagination-top">
            <Pagination
              currentPage={currentPage}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={handleUserPageChange}
              position="top"
            />
          </div>

          <ListDesktop>
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Avatar</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Provider</th>
                  <th>Total Sync Songs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <Spinner animation="border" role="status" />
                      <span className="ms-2">Loading...</span>
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.name}
                            className="rounded"
                            style={{ width: 36, height: 36, objectFit: 'cover' }}
                          />
                        ) : (
                          <span className="avatar avatar-sm bg-secondary rounded d-inline-flex align-items-center justify-content-center">
                            <FontAwesomeIcon icon={faUser} />
                          </span>
                        )}
                      </td>
                      <td>{user.name || '—'}</td>
                      <td>{user.email || '—'}</td>
                      <td>
                        <Badge
                          bg={getProviderBadgeVariant(user.provider)}
                          className="text-white"
                          style={{ padding: '0.35rem 0.65rem', fontSize: '0.875rem' }}
                        >
                          {user.provider || '—'}
                        </Badge>
                      </td>
                      <td>{user.total_sync_songs ?? 0}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openSongsModal(user)}
                            title="View songs"
                          >
                            <FontAwesomeIcon icon={faMusic} className="me-1" />
                            View Songs
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleMergeClick(user)}
                            title="Merge from Device"
                            disabled={mergeLoading}
                          >
                            <FontAwesomeIcon icon={faSync} className="me-1" />
                            Merge
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteClick(user)}
                            title="Delete All Sync Songs"
                            disabled={deleteLoading}
                          >
                            <FontAwesomeIcon icon={faTrash} className="me-1" />
                            Delete
                          </Button>
                          {user.alias_id && (
                            <Button
                              variant={copiedUserId === user.id ? 'success' : 'outline-secondary'}
                              size="sm"
                              onClick={() => handleCopyLink(user)}
                              title="Copy share link"
                            >
                              <FontAwesomeIcon icon={faCopy} className="me-1" />
                              {copiedUserId === user.id ? 'Copied!' : 'Copy Link'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </ListDesktop>

          {!loading && (
            <ListMobile>
              {users.map((user) => (
                <MobileCard key={user.id}>
                  <MobileCardTop>
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="rounded"
                        style={{ width: 36, height: 36, objectFit: 'cover' }}
                      />
                    ) : (
                      <span className="avatar avatar-sm bg-secondary rounded d-inline-flex align-items-center justify-content-center">
                        <FontAwesomeIcon icon={faUser} />
                      </span>
                    )}
                    <MobileCardTitle>{user.name || user.email || `User #${user.id}`}</MobileCardTitle>
                    <Badge bg={getProviderBadgeVariant(user.provider)} className="text-white">
                      {user.provider || '—'}
                    </Badge>
                  </MobileCardTop>
                  <MobileCardRow label="ID">{user.id}</MobileCardRow>
                  <MobileCardRow label="Email">{user.email || '—'}</MobileCardRow>
                  <MobileCardRow label="Sync songs">{user.total_sync_songs ?? 0}</MobileCardRow>
                  <MobileCardFooter>
                    <Button variant="outline-primary" size="sm" onClick={() => openSongsModal(user)}>
                      <FontAwesomeIcon icon={faMusic} className="me-1" />
                      Songs
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleMergeClick(user)}
                      disabled={mergeLoading}
                    >
                      <FontAwesomeIcon icon={faSync} className="me-1" />
                      Merge
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteClick(user)}
                      disabled={deleteLoading}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-1" />
                      Delete
                    </Button>
                    {user.alias_id && (
                      <Button
                        variant={copiedUserId === user.id ? 'success' : 'outline-secondary'}
                        size="sm"
                        onClick={() => handleCopyLink(user)}
                      >
                        <FontAwesomeIcon icon={faCopy} className="me-1" />
                        {copiedUserId === user.id ? 'Copied' : 'Link'}
                      </Button>
                    )}
                  </MobileCardFooter>
                </MobileCard>
              ))}
            </ListMobile>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={handleUserPageChange}
            position="bottom"
          />
        </Card.Body>
      </Card>

      {/* View Songs Modal */}
      <Modal show={!!songsModalUser} onHide={closeSongsModal} size="xl" centered scrollable fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>
            Songs {songsModalUser && `— ${songsModalUser.name && songsModalUser.email ? `${songsModalUser.name} (${songsModalUser.email})` : (songsModalUser.name || songsModalUser.email || `User #${songsModalUser.id}`)}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {songsError && (
            <Alert variant="danger">{songsError}</Alert>
          )}
          {songsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <span className="ms-2">Loading songs...</span>
            </div>
          ) : (
            <>
              <Pagination
                currentPage={songsPage}
                totalItems={songsTotal}
                itemsPerPage={songsPageSize}
                onPageChange={handleSongsPageChange}
                position="top"
              />
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Thumbnail</th>
                      <th>Title</th>
                      <th>Duration</th>
                      <th>Service</th>
                      <th>Type</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {songs.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          No songs found.
                        </td>
                      </tr>
                    ) : (
                      songs.map((item, index) => {
                        const song = parseSongItem(item);
                        return (
                          <tr key={item.task_id || index}>
                            <td>{(songsPage - 1) * songsPageSize + index + 1}</td>
                            <td>
                              {song.thumbnail_url ? (
                                <img
                                  src={song.thumbnail_url}
                                  alt={song.title}
                                  style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                                />
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>{song.title}</td>
                            <td>{song.duration != null ? `${Number(song.duration)}s` : '—'}</td>
                            <td>
                              <Badge bg="warning" text="dark" style={{ padding: '0.35rem 0.65rem', fontSize: '0.875rem' }}>
                                {song.service || '—'}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg="secondary" className="text-white" style={{ padding: '0.35rem 0.65rem', fontSize: '0.875rem' }}>
                                {song.type || '—'}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap align-items-center">
                                {song.result_url && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    as="a"
                                    href={song.result_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Play
                                  </Button>
                                )}
                                {song.stems && Object.entries(song.stems)
                                  .filter(([, url]) => url)
                                  .map(([key, url]) => {
                                    const isPrimaryVocal = key === 'vocal_url' || key === 'vocals_url';
                                    if (isPrimaryVocal && url === song.result_url) return null;
                                    return (
                                      <Button
                                        key={key}
                                        variant="outline-secondary"
                                        size="sm"
                                        as="a"
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={key}
                                      >
                                        {STEM_LINK_LABELS[key] || key.replace(/_url$/, '')}
                                      </Button>
                                    );
                                  })}
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={deletingSongId === (item.id ?? item.task_id)}
                                  onClick={() => handleDeleteSyncSong(item.id ?? item.task_id)}
                                  title="Delete sync song"
                                >
                                  {deletingSongId === (item.id ?? item.task_id) ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <FontAwesomeIcon icon={faTrash} />
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
              <Pagination
                currentPage={songsPage}
                totalItems={songsTotal}
                itemsPerPage={songsPageSize}
                onPageChange={handleSongsPageChange}
                position="bottom"
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSongsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {renderMergeModal()}

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered scrollable fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete All Sync Songs
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteStep === 'confirming' && (
            <div>
              <Alert variant="warning" className="mb-3">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                <strong>Warning:</strong> Are you sure you want to delete all sync songs for this user?
              </Alert>
              {currentDeleteUser && (
                <div className="mb-3">
                  <p><strong>User:</strong> {currentDeleteUser.name || currentDeleteUser.email || `User #${currentDeleteUser.id}`}</p>
                  <p><strong>User ID:</strong> {currentDeleteUser.id}</p>
                  <p><strong>Total Sync Songs:</strong> {currentDeleteUser.total_sync_songs ?? 0}</p>
                </div>
              )}
              <p className="mb-3"><strong>This action cannot be undone!</strong></p>
              <div className="d-flex gap-2 justify-content-center">
                <Button variant="danger" onClick={handleDeleteConfirm}>
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  Yes, Delete
                </Button>
                <Button variant="secondary" onClick={handleDeleteCancel}>
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {deleteStep === 'deleting' && (
            <div>
              <div className="mb-3">
                <p className="mb-2">Deleting data...</p>
                <ProgressBar now={deleteProgress} label={`${Math.round(deleteProgress)}%`} />
              </div>
              {deleteLoading && (
                <div className="text-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Processing...</span>
                </div>
              )}
            </div>
          )}

          {deleteStep === 'completed' && (
            <div>
              {deleteError ? (
                <Alert variant="danger">
                  <strong>Error:</strong> {deleteError}
                </Alert>
              ) : deleteData ? (
                <Alert variant="success">
                  <strong>Success!</strong> Deleted {deleteData.deleted_count || 0} songs.
                </Alert>
              ) : (
                <Alert variant="success">
                  <strong>Success!</strong> Deletion completed.
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SyncUsers;
